import { FC, FormEvent, useEffect } from 'react';
import { Typography, Box, Button, Divider } from '@mui/material';

import { AuthRole } from '@libs/types';
import { EmployerCreateDto } from '@libs/dto';
import { useEmployer } from '@carcareers/hooks';
import { UserInput, PasswordInput, RooftopsInput } from '@carcareers/components';
import { useDrawer, useForm } from '@carcareers/contexts';

interface EmployerCreateFormProps {
  onSuccess?: () => void;
}

export const EmployerCreateForm: FC<EmployerCreateFormProps> = ({ onSuccess = () => undefined }) => {
  const { setOpen } = useDrawer();
  const { data, setErrors } = useForm<EmployerCreateDto>();
  const { createEmployer } = useEmployer();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const requestData = {
      ...data,
      user: {
        ...data?.user,
        role: AuthRole.EMPLOYER,
      },
    };

    const response = await createEmployer(requestData, { onSuccess });

    if (response?.data) {
      setOpen(false);
    } else if (response?.errors) {
      setErrors(response.errors);
    }
  };

  useEffect(() => {
    return () => {
      setOpen(false);
    };
  }, [setOpen]);

  return (
    <>
      <Typography variant="h6">User Information</Typography>
      <UserInput />
      <PasswordInput />

      <Box
        sx={(theme) => ({
          display: 'flex',
          gap: theme.spacing(2),
          [theme.containerQueries('drawer').down(500)]: {
            flexDirection: 'column',
          },
        })}
      >
        <RooftopsInput<EmployerCreateDto['ownedRooftops']> type="ownedRooftops" />
      </Box>

      <Divider sx={{ flexGrow: 1 }} />

      <Box
        sx={(theme) => ({
          display: 'flex',
          gap: theme.spacing(2),
          justifyContent: 'flex-end',
          [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse' },
          [theme.containerQueries('drawer').down(500)]: { flexDirection: 'column-reverse' },
        })}
      >
        <Button size="large" variant="outlined" color="secondary" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button size="large" variant="contained" color="primary" onClick={handleSubmit}>
          Create Employer
        </Button>
      </Box>
    </>
  );
};

export default EmployerCreateForm;
